import type { InvitationPublicOut } from "@sagot/api-client";
import { PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon, initialsOf } from "@sagot/ui";
import { useState } from "react";

/** `module.action` keys → [module, actions[]] in first-seen order. */
export function groupPermissions(keys: readonly string[]): [string, string[]][] {
  const groups = new Map<string, string[]>();
  for (const key of keys) {
    const [module, action] = key.split(".");
    if (!module || !action) continue;
    groups.set(module, [...(groups.get(module) ?? []), action]);
  }
  return [...groups.entries()];
}

const RESTRICTED_MODULES = ["billing", "team", "api_keys"] as const;

function formatExpiry(expiresAt: string, locale: string) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  const days = Math.max(0, Math.ceil(ms / 86_400_000));
  const date = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric" }).format(new Date(expiresAt));
  return { days, date };
}

function PermissionList({ permissions }: { permissions: readonly string[] }) {
  const { t } = useTranslation();
  const groups = groupPermissions(permissions);
  const missing = RESTRICTED_MODULES.filter((module) => !groups.some(([m]) => m === module));
  return (
    <ul className="inv-perms">
      {groups.map(([module, actions]) => (
        <li key={module}>
          <Icon name="check" data-tone="ok" />
          <span>
            <b>{t(`roles.modules.${module}`, { defaultValue: module })}</b>: {actions.map((a) => t(`roles.actions.${a}`, { defaultValue: a })).join(", ")}
          </span>
        </li>
      ))}
      {missing.length > 0 ? (
        <li>
          <Icon name="x" data-tone="no" />
          <span>{t("auth.invite.cannot", { modules: missing.map((m) => t(`roles.modules.${m}`).toLowerCase()).join(", ") })}</span>
        </li>
      ) : null}
    </ul>
  );
}

/** Invite card in the shell's style: who, where, role, expiry, and what the role can do (accept-invite.html). */
export function InviteSummary({ invitation }: { invitation: InvitationPublicOut }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const role = invitation.role_names.join(", ");
  const article = /^[aeiou]/i.test(role) ? t("auth.invite.articleAn") : t("auth.invite.articleA");
  const expiry = formatExpiry(invitation.expires_at, i18n.language === "fil" ? "fil-PH" : "en-PH");
  const lead = invitation.inviter_name
    ? t("auth.invite.invitedYou", { inviter: invitation.inviter_name, workspace: invitation.workspace_name, product: PRODUCT_NAME })
    : t("auth.invite.invitedYouNoInviter", { workspace: invitation.workspace_name, product: PRODUCT_NAME });
  return (
    <section className="inv" aria-label={invitation.workspace_name}>
      <div className="inv-head">
        <span aria-hidden="true" className="inv-tile">
          {initialsOf(invitation.workspace_name)}
        </span>
        <div className="inv-lead">
          <p>
            {lead} <span className="inv-role">{role}</span>
          </p>
          {invitation.message ? <p className="inv-msg">“{invitation.message}”</p> : null}
        </div>
      </div>
      <dl className="inv-grid">
        <div>
          <dt>{t("auth.invite.workspace")}</dt>
          <dd>{invitation.workspace_name}</dd>
        </div>
        <div>
          <dt>{t("auth.invite.role")}</dt>
          <dd>{role}</dd>
        </div>
        <div>
          <dt>{t("auth.invite.invitedBy")}</dt>
          <dd>{invitation.inviter_name ?? "—"}</dd>
          {invitation.inviter_title ? <dd className="inv-sub">{invitation.inviter_title}</dd> : null}
        </div>
        <div>
          <dt>{t("auth.invite.expires")}</dt>
          <dd>{expiry.days === 0 ? t("auth.invite.expiresToday", { date: expiry.date }) : t("auth.invite.expiresIn", { count: expiry.days, date: expiry.date })}</dd>
        </div>
      </dl>
      <div className="inv-can">
        <button
          type="button"
          aria-expanded={open}
          aria-controls="invite-permissions"
          className="inv-toggle"
          onClick={() => {
            setOpen((v) => !v);
          }}
        >
          {t("auth.invite.whatCan", { article, role })}
          <Icon name={open ? "chevron-up" : "chevron-down"} />
        </button>
        <div id="invite-permissions" hidden={!open}>
          <PermissionList permissions={invitation.permissions} />
        </div>
      </div>
    </section>
  );
}
